import React, { useEffect, useState } from "react";
import { Control, Question } from "interfaces/Phoenix.FormsDesigner.Models";
import { DesignerQuestionDefinition, designerQuestionDefs, findQuestionDef } from "./DesignerQuestionDefinitions";
import IconButton from "components/Shared/IconButton";
import ReorderableGrid from "components/Shared/ReorderableGrid";
import { stopPropagationPreventDefault, stopPropagationPreventDefaultNoFn } from "tools/clickEvents";
import {  DesignerProviderValue } from ".";
import { classes } from "components/Utility";
import MSectionCollection, { MSECTION_MODE_TAB } from "components/Shared/MSectionCollection";
import PNXLabel from "components/PhoenixFormsV4/Controls/PNXLabel";

export type StagedQuestionsReference = { questions: Control[], clipboardQuestions: Question[] };

interface Props {
  stagedQuestionsReference: StagedQuestionsReference;
  filterOutContainerControls: boolean;
  context: React.MutableRefObject<DesignerProviderValue>; // need to pass context in because this component is mounted in the dialog, so it doesnt have access to the designer context
};

const reorderQuestionHandle = "reorder-question-handle";

const NewQuestionDialog = ({ stagedQuestionsReference, context, filterOutContainerControls }: Props) => {
  const [stagedQuestions, _setStagedQuestions] = useState<DesignerQuestionDefinition[]>([]);
  const [stagedClipboardQuestions, _setStagedClipboardQuestions] = useState<Question[]>([]);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  // sadly we need to keep this in state for display reasons
  // when we update the context, its changes arent propagated to the dialog because it lives outside of the context
  const [clipboardCopy, _setClipboardCopy] = useState<Question[]>(context.current.clipboard.data);

  const removeFromClipboard = (index: number) => {
    let c = [...clipboardCopy];
    c.slice(index, 1);
    _setClipboardCopy(c);
    context.current.clipboard.remove(index);
  };
  
  // the preference might not have all of the questions, or the preference might not exist at all
  // lets sort alphabetically for no preference
  const missingQuestions = designerQuestionDefs.map(qd => qd.name).sort((a, b) => a.localeCompare(b)).filter(n => !context.current.addQuestionOrder.some(qn => qn === n));
  console.log({missingQuestions});
  const addQuestionOrder = [...missingQuestions, ...context.current.addQuestionOrder];

  const stageQuestion = (question: DesignerQuestionDefinition) => _setStagedQuestions([...stagedQuestions, question]);
  const stageClipboardQuestion = (question: Question) => _setStagedClipboardQuestions([...stagedClipboardQuestions, question]);

  const unstageQuestion = (index: number) => {
    let stagedQuestionsCopy = [...stagedQuestions];
    stagedQuestionsCopy.splice(index, 1);
    _setStagedQuestions(stagedQuestionsCopy);
  };
  const unstageClipboardQuestion = (index: number) => {
    let stagedClipboardQuestionsCopy = [...stagedClipboardQuestions];
    stagedClipboardQuestionsCopy.splice(index, 1);
    _setStagedClipboardQuestions(stagedClipboardQuestionsCopy);
  };
  
  useEffect(() => {
    // only set the questions for the tab we are on, so when we save, questions from the tab we arent on dont get added too
    stagedQuestionsReference.questions = currentTabIndex === 0 ? stagedQuestions.map(q => q.control as Control) : [];
    stagedQuestionsReference.clipboardQuestions = currentTabIndex === 1 ? stagedClipboardQuestions : [];
  }, [stagedQuestions, stagedClipboardQuestions, currentTabIndex]); 

  let list = addQuestionOrder.map(name => {
    let questionDef = designerQuestionDefs.find(d => d.name === name);

    // extra guard in case theres a question def in test that doesnt exist in dev. Likely wouldnt ever be needed for prod
    // i guess if we ever removed a question type then it would just live in the users preference forever... thats probably fine
    if (!questionDef) return null;
    
    let disabled = filterOutContainerControls && questionDef.controlIsContainer;
    
    return {
      id: questionDef.name,
      node: (
        <div key={questionDef.name} className={classes("designer-new-question-button", disabled && "disabled-new-question")} onClick={!disabled ? (() => stageQuestion(questionDef)) : undefined}>
          <IconButton icon={questionDef.icon}/>
          <div className="question-info">
            <dt>{questionDef.name}</dt>
            <dd>{questionDef.description}</dd>
          </div>
          <IconButton icon="reorder" className={reorderQuestionHandle} onClick={stopPropagationPreventDefaultNoFn}/>
        </div>
      ),
    };
  }).filter(Boolean);

  const sections = [
    {
      title: "New Question",
      children: (
        <div className="new-question-staging-space">
          <div className="questions-to-add">
            <dl>
              <ReorderableGrid
                items={list}
                onReorder={context.current.setAddQuestionOrder}
                handleSelector={`.${reorderQuestionHandle}`}
              />
            </dl>
          </div>
          <div className="staged-questions">
            <h4>Staged Questions:</h4>
            <dl>
              {stagedQuestions.map((d, i) => 
                <div key={`staged-${i}`} className="designer-staged-question-button">
                  <IconButton icon="delete" onClick={() => unstageQuestion(i)}/>
                  <dt>{d.name}</dt>
                </div>
              )}
            </dl>
          </div>
        </div>
      ),
    },
    {
      title: "Clipboard",
      children: (
        <div className="new-question-staging-space">
          <div className="questions-to-add">
            <dl>
              {context.current.clipboard.data.map((q, i) => {
                let questionDef = findQuestionDef(q);
                
                return (
                  <div className="clipboard-item" onClick={() => stageClipboardQuestion(q)}>
                    <IconButton icon={questionDef?.icon} />
                    <div>{questionDef.name}</div>
                    <PNXLabel>{q.control.label}</PNXLabel>
                    <IconButton icon="delete" onClick={stopPropagationPreventDefault(() => removeFromClipboard(i))}/>
                  </div>
                  );
                }
              )}
            </dl>
          </div>
          <div className="staged-questions">
            <h4>Staged Questions:</h4>
            <dl>
              {stagedClipboardQuestions.map((d, i) => 
                <div key={`staged-${i}`} className="designer-staged-question-button">
                  <IconButton icon="delete" onClick={() => unstageClipboardQuestion(i)}/>
                  <dt><PNXLabel>{d.control.label}</PNXLabel></dt>
                </div>
              )}
            </dl>
          </div>
        </div>
      ),
    }
  ];

  return (
    <MSectionCollection
      sections={sections}
      defaultMode={MSECTION_MODE_TAB}
      tabModeCollapsible={false}
      onTabChange={setCurrentTabIndex}
      fullHeight
      disableModeChanging
    />
  );
};

export default NewQuestionDialog;
